/**
 * Compras — Solicitação de compra interna (D5a).
 *
 * Formulário INTERNO autenticado: qualquer colaborador logado pede uma compra
 * (itens + qtd + urgência + prazo, SEM preço). "Obra" só aparece p/ Projetos e
 * Manutenção. Gated por COMPRAS_ENABLED (no MenuPage). ZERO localStorage.
 *
 * 🔴 Fase 2 (decisão do Matheus, 15/09/2026 23h): o pedido de compra tem UM endereço —
 * o **Quadro**. A aba “Fila do Compras” que morava nesta página MORREU: quem classifica faz isso
 * no card da coluna “A classificar”, onde o pedido já está. Aqui ficou o que sempre foi desta
 * tela — pedir e acompanhar o que eu pedi. ❌ Não recriar fila paralela aqui.
 */
import { useState } from 'react';

import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SelectOption,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui';
import { errMsg } from '@/lib/api-error';
import { parseNumeroBr } from '@/lib/format';
import { VinculoPicker } from './VinculoPicker';
import { VINCULO_VAZIO, buildVinculo, type VinculoState } from './vinculo-solicitacao';
import {
  useSolicitacaoContexto,
  useMinhasSolicitacoes,
  useSolicitacaoActions,
  type SolicitacaoCompra,
  type SolicitacaoCompraUrgencia,
  type SolicitacaoCompraStatus,
  type TipoVinculo,
} from '@/lib/solicitacoes-compra-store';

import { formatDate } from '@/lib/format-date';
type ItemRascunho = { item: string; quantidade: string; linkReferencia: string };
const ITEM_VAZIO: ItemRascunho = { item: '', quantidade: '1', linkReferencia: '' };

const URGENCIA_LABEL: Record<SolicitacaoCompraUrgencia, string> = {
  baixa: 'Baixa',
  media: 'Média',
  alta: 'Alta',
};
const URGENCIA_VARIANT: Record<SolicitacaoCompraUrgencia, 'neutral' | 'warning' | 'danger'> = {
  baixa: 'neutral',
  media: 'warning',
  alta: 'danger',
};
const STATUS_LABEL: Record<SolicitacaoCompraStatus, string> = {
  aberta: 'Aberta',
  classificada: 'Em compras',
  concluida: 'Concluída',
  cancelada: 'Cancelada',
};
const STATUS_VARIANT: Record<SolicitacaoCompraStatus, 'info' | 'success' | 'neutral'> = {
  aberta: 'info',
  classificada: 'info',
  concluida: 'success',
  cancelada: 'neutral',
};
// FASE-C (copy): "Deal" era jargão de dev vazando pra UI — pro usuário é
// "Negócio" (mesmo vocabulário do Funil). Só o RÓTULO muda; o valor
// `deal` segue idêntico no payload/banco.
const TIPO_VINCULO_LABEL: Record<TipoVinculo, string> = {
  projeto: 'Projeto',
  deal: 'Negócio',
  cliente: 'Cliente',
  interno: 'Uso interno',
};

function fmtData(iso: string): string {
  return formatDate(iso);
}

export function SolicitacaoCompraPage() {
  // Fase 2 (16/09/2026): o gate `ehCompras` (dívida #77, o espelho do
  // `temPerfilOuDepartamento` do backend) saiu junto com a aba "Fila do Compras" — era ela, e só
  // ela, que o usava. Esta página é de QUEM PEDE: qualquer colaborador abre uma solicitação e vê
  // as suas. O trabalho do Compras agora tem um endereço só, o Quadro.
  return (
    // LAYOUT-CANONICO: o `w-full max-w-5xl` próprio saiu — a largura é a régua
    // única do PageContainer que o MenuPage monta em volta desta tela.
    <>
      {/* FASE-C: PageHeader canônico (crumb + h1 + subtítulo). */}
      <PageHeader
        crumb="Compras"
        title="Solicitação de compra"
        subtitle="Peça uma compra pro time de Compras. Liste os itens e a quantidade — sem preço. O Compras cota e fecha depois."
      />

      <Tabs defaultValue="nova">
        <TabsList>
          <TabsTrigger value="nova">Nova solicitação</TabsTrigger>
          <TabsTrigger value="minhas">Minhas solicitações</TabsTrigger>
        </TabsList>

        <TabsContent value="nova">
          <NovaSolicitacaoForm />
        </TabsContent>
        <TabsContent value="minhas">
          <MinhasSolicitacoes />
        </TabsContent>
      </Tabs>
    </>
  );
}

function NovaSolicitacaoForm() {
  const toast = useToast();
  const ctx = useSolicitacaoContexto();
  const { criar } = useSolicitacaoActions();

  const [itens, setItens] = useState<ItemRascunho[]>([{ ...ITEM_VAZIO }]);
  const [urgencia, setUrgencia] = useState<SolicitacaoCompraUrgencia>('media');
  const [prazo, setPrazo] = useState('');
  const [vinculo, setVinculo] = useState<VinculoState>(VINCULO_VAZIO);
  const [observacao, setObservacao] = useState('');

  function setItem(idx: number, patch: Partial<ItemRascunho>) {
    setItens((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItens((arr) => [...arr, { ...ITEM_VAZIO }]);
  }
  function removeItem(idx: number) {
    setItens((arr) => (arr.length <= 1 ? arr : arr.filter((_, i) => i !== idx)));
  }
  function resetForm() {
    setItens([{ ...ITEM_VAZIO }]);
    setUrgencia('media');
    setPrazo('');
    setVinculo(VINCULO_VAZIO);
    setObservacao('');
  }

  function enviar() {
    const limpos = itens
      .map((it) => ({
        item: it.item.trim(),
        quantidade: parseNumeroBr(it.quantidade) ?? 1,
        linkReferencia: it.linkReferencia.trim() || null,
      }))
      .filter((it) => it.item.length > 0);
    if (limpos.length === 0) {
      toast.show('Adicione pelo menos um item.', { variant: 'error' });
      return;
    }
    criar.mutate(
      {
        urgencia,
        prazo: prazo.trim() || null,
        vinculo: buildVinculo(vinculo),
        observacao: observacao.trim() || null,
        itens: limpos,
      },
      {
        onSuccess: () => {
          toast.show('Solicitação enviada pro Compras.', { variant: 'success' });
          resetForm();
        },
        onError: (e) => toast.show(errMsg(e) ?? 'Não deu pra enviar.', { variant: 'error' }),
      },
    );
  }

  return (
    <Card className="p-4 md:p-6 space-y-5">
      <div className="text-sm text-ink-2 dark:text-ink-dark-2">
        Solicitante: <span className="font-medium text-ink-1 dark:text-ink-dark-1">{ctx.data?.nome ?? '—'}</span>
        {ctx.data?.departamentos.length ? (
          <span className="text-ink-3 dark:text-ink-dark-3"> · {ctx.data.departamentos.join(', ')}</span>
        ) : null}
      </div>

      {/* Itens */}
      <div className="space-y-3">
        <Label>Itens</Label>
        {itens.map((it, idx) => (
          <div key={idx} className="flex flex-wrap items-end gap-2">
            <div className="flex-1 min-w-[180px]">
              <Input
                size="sm"
                placeholder="Item (ex.: câmera dome, cabo UTP…)"
                value={it.item}
                onChange={(e) => setItem(idx, { item: e.target.value })}
              />
            </div>
            {/* 18/09: cinco dígitos inteiros (obra real tem cabo em milhares de metros); 80 px cortava. */}
            <div className="w-28">
              <Input
                size="sm"
                type="number"
                className="text-right tabular-nums"
                placeholder="Qtd"
                value={it.quantidade}
                onChange={(e) => setItem(idx, { quantidade: e.target.value })}
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Input
                size="sm"
                placeholder="Link de referência (opcional)"
                value={it.linkReferencia}
                onChange={(e) => setItem(idx, { linkReferencia: e.target.value })}
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
              onClick={() => removeItem(idx)}
              disabled={itens.length <= 1}
            >
              Remover
            </Button>
          </div>
        ))}
        <Button size="sm" variant="ghost" onClick={addItem}>
          + Adicionar item
        </Button>
      </div>

      {/* Metadados */}
      <div className="flex flex-wrap gap-4">
        <div className="w-32">
          <Label>Urgência</Label>
          <Select size="sm" value={urgencia} onChange={(e) => setUrgencia(e.target.value as SolicitacaoCompraUrgencia)}>
            <SelectOption value="baixa">Baixa</SelectOption>
            <SelectOption value="media">Média</SelectOption>
            <SelectOption value="alta">Alta</SelectOption>
          </Select>
        </div>
        <div className="w-44">
          <Label>Prazo (opcional)</Label>
          <Input size="sm" type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
        </div>
        <div className="flex-1 min-w-[240px]">
          <VinculoPicker value={vinculo} onChange={setVinculo} />
        </div>
      </div>

      <div>
        <Label>Observação (opcional)</Label>
        <Input size="sm" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Algum detalhe pro Compras" />
      </div>

      <div className="flex justify-end">
        <Button onClick={enviar} disabled={criar.isPending}>
          {criar.isPending ? 'Enviando…' : 'Enviar solicitação'}
        </Button>
      </div>
    </Card>
  );
}

export function MinhasSolicitacoes() {
  const toast = useToast();
  const minhas = useMinhasSolicitacoes();
  const { cancelar } = useSolicitacaoActions();

  // 🔴 #347 (18/09): a tela só promete o que acontece. A cancelada NÃO some: fica no Quadro do Compras, marcada, com o motivo.
  const [cancelando, setCancelando] = useState<SolicitacaoCompra | null>(null);
  const [motivo, setMotivo] = useState('');
  function onCancelar(s: SolicitacaoCompra) {
    setMotivo('');
    setCancelando(s);
  }
  function confirmarCancelamento() {
    if (!cancelando) return;
    cancelar.mutate(
      { id: cancelando.id, motivo },
      {
        onSuccess: () => {
          setCancelando(null);
          toast.show('Solicitação cancelada. Ela continua no Quadro do Compras, marcada como cancelada.', { variant: 'success' });
        },
        onError: (e) => toast.show(errMsg(e) ?? 'Não deu certo. Tente de novo.', { variant: 'error' }),
      },
    );
  }

  if (minhas.isLoading) return <p className="text-sm text-ink-3 dark:text-ink-dark-3 py-6">Carregando…</p>;
  if (minhas.isError) {
    return (
      <div className="py-6 text-sm">
        <p className="text-red mb-2">Não foi possível carregar suas solicitações. Verifique a conexão e tente de novo.</p>
        <Button size="sm" variant="ghost" onClick={() => minhas.refetch()}>Tentar de novo</Button>
      </div>
    );
  }
  const items = minhas.data?.items ?? [];
  if (items.length === 0) {
    return <EmptyState title="Nenhuma solicitação ainda" description="As compras que você pedir aparecem aqui." />;
  }
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <Card key={s.id} variant="flat" className="p-3 flex flex-wrap items-center gap-3">
          <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
          <Badge variant={URGENCIA_VARIANT[s.urgencia]}>{URGENCIA_LABEL[s.urgencia]}</Badge>
          <span className="text-sm text-ink-1 dark:text-ink-dark-1">
            {s.tipoVinculo ? TIPO_VINCULO_LABEL[s.tipoVinculo] : s.obra || 'Sem vínculo'}
          </span>
          <span className="text-xs text-ink-3 dark:text-ink-dark-3">{fmtData(s.criadoEmIso)}</span>
          {s.status === 'cancelada' && s.canceladaMotivo && (
            <span className="basis-full text-xs text-ink-2 dark:text-ink-dark-2 break-words">Motivo do cancelamento: {s.canceladaMotivo}</span>
          )}
          {s.status === 'aberta' && (
            <Button size="sm" variant="ghost" className="ml-auto min-h-[44px] sm:min-h-0 text-red hover:bg-red-soft dark:hover:bg-red-soft-dark" onClick={() => onCancelar(s)}>
              Cancelar
            </Button>
          )}
        </Card>
      ))}
      {cancelando && (
        <Dialog open onOpenChange={(o) => { if (!o) setCancelando(null); }}>
          <DialogHeader>
            <DialogTitle>Cancelar a solicitação{cancelando.numeroHumano != null ? ` #${cancelando.numeroHumano}` : ''}?</DialogTitle>
            <DialogDescription>
              Ela continua no Quadro do Compras, marcada como cancelada, com o motivo. Os itens deixam de ser comprados. Não dá para desfazer
              pela tela.
            </DialogDescription>
          </DialogHeader>
          <DialogContent>
            <Label htmlFor="motivo-cancelamento">Motivo (opcional, mas ajuda o Compras)</Label>
            <Input
              id="motivo-cancelamento"
              value={motivo}
              maxLength={500}
              placeholder="Ex.: pedi em dobro; já foi comprado na outra solicitação"
              onChange={(e) => setMotivo(e.target.value)}
              data-testid="motivo-cancelamento"
            />
          </DialogContent>
          <DialogFooter>
            <Button variant="ghost" className="min-h-[44px] sm:min-h-0" onClick={() => setCancelando(null)} disabled={cancelar.isPending}>
              Voltar
            </Button>
            <Button variant="destructive" className="min-h-[44px] sm:min-h-0" onClick={confirmarCancelamento} loading={cancelar.isPending} data-testid="confirmar-cancelamento">
              Cancelar solicitação
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </div>
  );
}
